/* eslint-disable no-unreachable-loop */
import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import Header from '@/components/Header/Header';
import styles from './NationalModifiers.module.css';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useNationalModifiers, useUpdateNationalModifiersLock } from '@/services/nationalModifiersService';
import { NationalModifiersDetails, NationalModifiersSelection } from '@/types/nationalModifiers.types';
import { SwitchCell } from './internal/SwitchCell';
import Button from '@/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { useNationalModifiersSaveState } from '@/hooks/useSaveButtonState';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';

const NationalModifiers: FC = () => {
  const { t } = useTranslation();
  const { data: dataNationalModifiers, isLoading, isError, refetch } = useNationalModifiers(true);
  const [data, setData] = useState(dataNationalModifiers?.data || []);
  const [originalData, setOriginalData] = useState(dataNationalModifiers?.data || []);
  const { mutate: updateIsLockForNationalModifiers } = useUpdateNationalModifiersLock();

  // Use optimized hooks
  const debouncedUpdate = useDebouncedUpdate(setData, 100);
  const isIdenticalData = useNationalModifiersSaveState(originalData, data);

  const columns = useMemo(
    () => [
      {
        cell: SwitchCell,
        header: t('modifiers.table.lockPrice'),
        accessorKey: 'isLock',
        meta: { disableFilter: true },
      },
      {
        accessorFn: (originalRow: NationalModifiersDetails) => originalRow.slotName || '-',
        header: t('common.slot'),
        enableSorting: true,
      },
      {
        accessorFn: (originalRow: NationalModifiersDetails) => {
          const shouldShowWeightName =
            originalRow.modifierWeightName && originalRow.modifierWeightName.toLowerCase() !== 'none';
          return (
            `${originalRow.modifierName}${shouldShowWeightName ? `, ${originalRow.modifierWeightName}` : ''}` || '-'
          );
        },
        header: t('modifiers.table.modifier'),
        enableSorting: true,
      },
    ],
    [t],
  );

  if (isError) {
    showToast({
      label: t('modifiers.errorFetching'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingNationalModifiers',
    });
  }

  useEffect(() => {
    if (dataNationalModifiers?.data) {
      setData(dataNationalModifiers.data);
      setOriginalData(dataNationalModifiers.data);
    }
  }, [dataNationalModifiers]);

  const onButtonClick = useCallback(() => {
    // Flush any pending debounced updates before saving
    const updateFn = debouncedUpdate as typeof debouncedUpdate & { flush?: () => void };

    if (updateFn.flush) {
      flushSync(() => {
        updateFn.flush!();
      });
    }

    // Build the update payload with the current data state
    const nationalModifiersToUpdateSelection: NationalModifiersSelection[] = [];

    originalData.forEach((modifier) => {
      const resultObject = data.find((modifierInternal) => modifierInternal.id === modifier.id);

      if (!resultObject) return;

      // Normalize to booleans for comparison
      const resultIsLock = resultObject.isLock != null ? Boolean(resultObject.isLock) : false;
      const originalIsLock = modifier.isLock != null ? Boolean(modifier.isLock) : false;

      if (resultIsLock !== originalIsLock) {
        // Ensure we always send proper boolean values (never null/undefined)
        const nms: NationalModifiersSelection = {
          id: resultObject.id,
          isLock: resultIsLock,
        };
        nationalModifiersToUpdateSelection.push(nms);
      }
    });

    if (nationalModifiersToUpdateSelection.length > 0) {
      updateIsLockForNationalModifiers(nationalModifiersToUpdateSelection, {
        onSuccess: () => {
          showToast({
            id: 'editNationalModifiersLock',
            label: t('modifiers.lockStatusUpdated'),
          });

          refetch();
        },
        onError: () => {
          showToast({
            id: 'errorUpdatingNationalModifiersLock',
            label: t('modifiers.errorUpdatingLockStatus'),
            toastType: ToastTypes.Error,
          });
        },
      });
    }
  }, [originalData, data, debouncedUpdate, updateIsLockForNationalModifiers, t, refetch]);

  return (
    <>
      <Header title="National Modifiers" breadcrumb={{ to: '/', label: 'Back to Home' }} />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          All Modifiers
          <Button disabled={isIdenticalData} onClick={onButtonClick}>
            Save Changes
          </Button>
        </div>
        <TableCard
          height="calc(75vh - 100px)"
          isLoading={isLoading}
          data={data}
          meta={{
            columns,
            data,
            updateData: debouncedUpdate,
          }}
          columns={columns}
          enableFilters
        />
      </div>
    </>
  );
};

export default NationalModifiers;
